import DOMPurify from 'dompurify';

// Only embeddable viewer pages are framed, never arbitrary pages on these hosts (e.g. a Forms editor or Drive UI).
const EMBED_PATHS: Record<string, RegExp> = {
  'drive.google.com': /^\/file\/d\/[^/]+\/preview\/?$/,
  'docs.google.com': /^\/(document|spreadsheets|presentation|forms)\/d\/(e\/)?[^/]+\/(preview|pub|embed|viewform)\/?$/,
};

// Drive share links (/view, /edit, open?id=) refuse to render in a frame; the
// /preview form is the embeddable one.
const toDriveEmbedUrl = (src: string): string | null => {
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return null;
  }
  const allowedPath = EMBED_PATHS[url.hostname];
  if (url.protocol !== 'https:' || !allowedPath) return null;
  const fileMatch = url.pathname.match(/^\/file\/d\/([^/]+)/);
  const fileId = fileMatch?.[1] ?? (url.pathname === '/open' ? url.searchParams.get('id') : null);
  if (url.hostname === 'drive.google.com') {
    return fileId ? `https://drive.google.com/file/d/${encodeURIComponent(fileId)}/preview` : null;
  }
  return allowedPath.test(url.pathname) ? url.toString() : null;
};

// Own instance so the iframe hook never touches other DOMPurify callers.
const purify = DOMPurify(window);
purify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName !== 'IFRAME') return;
  const embedUrl = toDriveEmbedUrl(node.getAttribute('src') ?? '');
  if (embedUrl) {
    node.setAttribute('src', embedUrl);
    node.setAttribute('loading', 'lazy');
  } else {
    node.remove();
  }
});

/** Sanitizes pop-up HTML; the only iframes kept are Google Drive / Docs embeds. */
export const sanitizePopupHtml = (html: string): string =>
  purify.sanitize(html, { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'loading'] });
